<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { navItems, seo } from '~/data/pages/banks/mocks'
import { WidgetBankPageAboutBankHeader } from '~/components/widget/bank-page'
import type { IBank } from '~/components/entity/bank'
import type { IResponseData } from '~/types'
import {
  WidgetOrganizationAsideContacts,
  mapBankToOrganizationInfo
} from '~/components/widget/organization-aside-contacts'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { WidgetCardsList } from '~/components/widget/cards'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'
import { FeatureCardFormRequest } from '~/components/feature/card/form-request'
import { WidgetScrollTo } from '~/components/widget/scroll-to'
import { WidgetSelectCardType } from '~/components/widget/select-card-type'
import { ANCHOR_IDS } from '~/lib/constants'

const { t, locale } = useI18n()

useSeoMeta(seo)
const route = useRoute()
const bankUrl = `/banks/${route.params.bank}`
const { data } = await useApiWith404<IResponseData<IBank>>(() => useUrlWithParams(bankUrl), {
  key: `banks-cards-${route.params.bank}`
})
const bank = computed(() => data.value?.data)
useEmptySectionNoindex(bank, 'cards_count')
const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: bank.value?.title
  }
}))

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
  { title: bank.value?.title || t('common.bank'), url: `/bank/${route.params.bank}` },
  { title: t('common.bank_cards'), url: `/bank/${route.params.bank}/cards` }
])
const organizationInfo = computed(() => mapBankToOrganizationInfo(bank.value))

const { cardInstructionsComponent } = useCardInstructionsComponent(
  route.params.bank as string,
  bank.value?.permissions?.is_card_instructions_enabled
)
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="organizationInfo"
              :organization="organizationInfo"
            />
            <WidgetScrollTo
              v-if="bank && bank.permissions?.is_card_request_allowed && bank.status === 'active'"
              :title="$t('page.bank.cards.card_request_title')"
              :description="$t('page.bank.cards.card_request_description')"
              :button-text="$t('page.bank.cards.card_request_button')"
              :target="ANCHOR_IDS.FEATURE_CARD_FORM_REQUEST"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSelectCardType
              :query-params="{
                'filter[position]': 'sidebar'
              }"
            />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetBankPageAboutBankHeader :bank="bank">
            <WidgetBankServicesMenu
              v-if="bank && bank.status === 'active'"
              :organization="bank"
              :nav-items="navItems"
            />
          </WidgetBankPageAboutBankHeader>
          <WidgetSeoContent
            heading-level="h2"
            heading-class="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: bank?.official_title ?? ''
            }"
          />
          <WidgetCardsList
            v-if="bank && bank.id"
            :query-params="{
              per_page: '50', // intentionally avoid pagination
              'filter[organization_id]': bank.id.toString(),
              'filter[organization_type]': bank.organization_type
            }"
            position="banks"
          />
          <FeatureCardFormRequest
            v-if="bank && bank.permissions?.is_card_request_allowed"
            :organization-type="bank.organization_type"
            :organization-id="bank.id"
          />
          <component :is="cardInstructionsComponent" v-if="cardInstructionsComponent" />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
